import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from "notistack";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import TextField from "@/components/TextField";
import MenuDropdown from "@/components/MenuDropdown";
import Autocomplete from "@/components/Autocomplete";
import type { MembershipHistoryRow as MembershipRow } from "@/features/person/components/MembershipHistoryTable";
import { useQuery, useMutation, gql, TypedDocumentNode } from "@apollo/client";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import { GET_CLASSIFICATIONS } from "@/pages/billing/queries";
import { GET_MEMBERSHIP_DETAILS_QUERY } from "../../person/queries";

type SectionAutocompleteResult = {
  id: string;
  name: string;
  legacyId: string | null;
};

const SECTION_AUTOCOMPLETE_QUERY: TypedDocumentNode<
  {
    sections: SectionAutocompleteResult[];
  },
  {}
> = gql`
  query sectionAutocompleteQuery {
    sections {
      id
      name
      legacyId
    }
  }
`;

const GET_MEMBERSHIP_BAND_REASON_OPTIONS: TypedDocumentNode<
  {
    bandCreatedReasons: string[];
  },
  {}
> = gql`
  query BandCreatedReasons {
    bandCreatedReasons
  }
`;

const UPSERT_MEMBER_BAND_MUTATION = gql`
  mutation UpsertMemberBand($input: BackofficeUpsertMemberBandInput!) {
    upsertMemberBand(input: $input) {
      success
      message
      band {
        id
        startedOn
        expiresOn
        programType {
          id
          name
        }
        classification {
          id
          name
        }
        section {
          id
          legacyId
        }
        sectionAffiliation {
          id
        }
        createdReason
        terminatedReason
        terminatesOn
      }
    }
  }
`;

type FieldValues = {
  bandId: string;
  sectionId: string;
  sectionName: string;
  classificationId: string;
  startDate: string;
  endDate: string;
  reason: string;
  note: string;
};

interface EditMembershipModalProps {
  open: boolean;
  onClose: () => void;
  membershipRow: MembershipRow | null;
  personId: string;
  pgaId: string;
}

export default function EditMembershipModal({
  open,
  onClose,
  membershipRow,
  personId,
  pgaId
}: EditMembershipModalProps) {
  const [bannerError, setBannerError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const { data: sectionData, loading: sectionLoading } = useQuery(SECTION_AUTOCOMPLETE_QUERY);
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const { data: reasonOptionsData } = useQuery(GET_MEMBERSHIP_BAND_REASON_OPTIONS);

  // Query person data to get band information including programTypeId
  const { data: personData } = useQuery(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !membershipRow?.id
  });

  // Find the band to get programTypeId and sectionId
  const currentBand = personData?.person?.bands?.find((band: { id: string }) => band.id === membershipRow?.id);
  const nonCurrentBands =
    personData?.person?.bands?.filter((band: { id: string }) => band.id !== membershipRow?.id) || [];
  const programTypeId = currentBand?.programType?.id;
  // Get section ID from band (prefer sectionAffiliation.section.id, fallback to section.id)
  const bandSectionId = currentBand?.sectionAffiliation?.section?.id || currentBand?.section?.id;

  const getBandEndDate = (band: { terminatesOn?: string | null; expiresOn?: string | null }) => {
    return band.terminatesOn || band.expiresOn || null;
  };

  const currentBandOriginalStartDate = membershipRow?.startDateRaw || currentBand?.startedOn;

  const previousBands = nonCurrentBands.filter(
    (band: { terminatesOn?: string | null; expiresOn?: string | null; startedOn?: string }) => {
      const endDate = getBandEndDate(band);
      return endDate && currentBandOriginalStartDate && endDate < currentBandOriginalStartDate;
    }
  );

  const previousBandEndDates = previousBands
    .map((band: { terminatesOn?: string | null; expiresOn?: string | null }) => getBandEndDate(band))
    .filter((date: string | null): date is string => date !== null)
    .sort()
    .reverse();

  const sections = sectionData?.sections || [];
  const classifications = classificationData?.classifications?.nodes || [];

  const classificationOptions = classifications.map((c: { id: string; name: string }) => ({
    id: c.id,
    name: c.name
  }));

  const formatReasonLabel = (enumValue: string) => {
    return enumValue
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const baseReasonOptions =
    reasonOptionsData?.bandCreatedReasons?.map((reason: string) => ({
      value: reason,
      label: formatReasonLabel(reason)
    })) || [];

  const currentBandReason = currentBand?.createdReason;
  const hasNonExistentReason = baseReasonOptions.every((opt) => opt.value !== currentBandReason);

  const reasonOptions =
    currentBandReason && hasNonExistentReason
      ? [
          ...baseReasonOptions,
          {
            value: currentBandReason,
            label: formatReasonLabel(currentBandReason)
          }
        ].sort((a, b) => a.label.localeCompare(b.label))
      : baseReasonOptions.sort((a, b) => a.label.localeCompare(b.label));

  const getReasonValue = (formattedReason: string | null | undefined) => {
    if (!formattedReason) return "";
    return formattedReason.toLowerCase().replace(/\s+/g, "_");
  };

  const getClassificationId = (classificationName: string | undefined) => {
    if (!classificationName) return "";
    const found = classificationOptions.find((c: { id: string; name: string }) => c.name === classificationName);
    return found?.id || "";
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FieldValues>({
    defaultValues: {
      bandId: membershipRow?.id || "",
      sectionId: "",
      sectionName: membershipRow?.section || "",
      classificationId: getClassificationId(membershipRow?.classification),
      startDate: membershipRow?.startDateRaw || "",
      endDate: membershipRow?.endDateRaw || "",
      reason: getReasonValue(membershipRow?.startReason),
      note: ""
    },
    values: membershipRow
      ? {
          bandId: membershipRow.id || "",
          sectionId: bandSectionId || "",
          sectionName: membershipRow.section || "",
          classificationId: getClassificationId(membershipRow.classification),
          startDate: membershipRow.startDateRaw || "",
          endDate: membershipRow.endDateRaw || "",
          reason: getReasonValue(membershipRow.startReason),
          note: currentBand?.note || ""
        }
      : undefined,
    mode: "onChange"
  });

  useEffect(() => {
    if (bandSectionId && membershipRow) {
      setValue("sectionId", bandSectionId, { shouldValidate: true });
    }
  }, [bandSectionId, membershipRow, setValue]);

  const [sectionQuery, setSectionQuery] = useState(membershipRow?.section || "");
  const filteredSections = sections.filter(
    (s) =>
      s.name?.toLowerCase().includes(sectionQuery.toLowerCase()) ||
      s.legacyId?.toLowerCase().includes(sectionQuery.toLowerCase())
  );

  const [upsertMemberBand, { loading: mutationLoading }] = useMutation(UPSERT_MEMBER_BAND_MUTATION, {
    refetchQueries: [
      {
        query: GET_MEMBERSHIP_DETAILS_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  async function submit(formData: FieldValues) {
    try {
      const initialDate = formData.startDate ? new Date(formData.startDate).toISOString() : undefined;

      const input = {
        id: formData.bandId,
        personId,
        programTypeId,
        startedOn: formData.startDate,
        expiresOn: formData.endDate || formData.startDate,
        classificationId: formData.classificationId,
        sectionId: formData.sectionId,
        createdReason: formData.reason || undefined,
        initialDate,
        note: formData.note || undefined
      };

      const { data } = await upsertMemberBand({ variables: { input } });

      if (data?.upsertMemberBand?.success) {
        enqueueSnackbar(data.upsertMemberBand.message || "Membership updated successfully", { variant: "default" });
        onClose();
      } else {
        setBannerError(data?.upsertMemberBand?.message || "Failed to update membership");
      }
    } catch (e) {
      const errorMessage = (e as Error).message || "An error occurred while updating membership";
      setBannerError(`Error: ${errorMessage}`);
    }
  }

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[648px]"
      open={open}
      onClosed={onClose}
      headline="Edit Membership"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="edit-membership-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submit)}
            testId="edit-membership-save-button"
            disabled={isSubmitting || !isValid || mutationLoading || !programTypeId}
          >
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The update failed." subText={bannerError} />
        </div>
      )}

      <div className="flex flex-col gap-6" data-testid="edit-membership-modal">
        {/* Hidden field to prevent first dropdown from auto-selecting */}
        <Controller name="bandId" control={control} render={({ field }) => <input type="hidden" {...field} />} />
        <Controller
          name="sectionName"
          control={control}
          rules={{ required: "Section is required" }}
          render={({ field: { onChange } }) => (
            <Autocomplete<SectionAutocompleteResult>
              variant="searchableDropdown"
              data={filteredSections}
              loadingResults={sectionLoading}
              query={sectionQuery}
              label="Section"
              renderItem={(opt) => (
                <div className="flex flex-col">
                  <div className="text-body-large">{opt.name}</div>
                  <div className="text-label-medium text-gray-600">{opt.legacyId}</div>
                </div>
              )}
              reset={() => {
                onChange("");
                setSectionQuery("");
                setValue("sectionId", "");
              }}
              onChangeText={(text) => {
                setSectionQuery(text);
                onChange(text);
              }}
              onSelect={(value) => {
                setValue("sectionId", value.id);
                onChange(value.name);
                setSectionQuery(value.name);
              }}
              error={!!errors.sectionName}
              errorText={errors.sectionName?.message}
            />
          )}
        />

        <Controller
          name="classificationId"
          control={control}
          rules={{ required: "Classification is required" }}
          render={({ field: { onChange, value } }) => (
            <MenuDropdown
              options={classificationOptions}
              value={classificationOptions.find((c: { id: string; name: string }) => c.id === value)}
              onSelect={(option) => onChange(option.id)}
              label="Classification"
              getLabel={(option) => option.name}
              renderItem={(option) => (
                <div className="flex items-start gap-3 w-full" data-testid={`classification-option-${option.id}`}>
                  <div className="flex-1">
                    <div className="text-body-large">{option.name}</div>
                  </div>
                </div>
              )}
            />
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: "Start Date is required",
              validate: (value) => {
                if (!value) return "Start Date is required";
                const endDate = watch("endDate");
                if (endDate && value > endDate) {
                  return "Start date cannot be later than end date";
                }
                if (previousBandEndDates.length > 0) {
                  const latestPreviousEndDate = previousBandEndDates[0];
                  if (value <= latestPreviousEndDate) {
                    return `Start date cannot be before the end date of a previous band.`;
                  }
                }
                return true;
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <DateSelect
                label="Start Date *"
                value={{ startDate: field.value, endDate: field.value }}
                onChange={(x: DateValueType | string) => {
                  if (typeof x === "string") return;
                  if (x === null) {
                    field.onChange("");
                    trigger("endDate");
                    return;
                  }
                  field.onChange(x.startDate);
                  trigger("endDate");
                }}
                onBlur={field.onBlur}
                useSingleDate
                useMultiCalendar={false}
                useModal
                error={!!error}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true;
                const startDate = watch("startDate");
                if (startDate && value < startDate) {
                  return "End date cannot be earlier than start date";
                }
                return true;
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <DateSelect
                label="End Date"
                value={{ startDate: field.value, endDate: field.value }}
                onChange={(x: DateValueType | string) => {
                  if (typeof x === "string") return;
                  if (x === null) {
                    field.onChange("");
                    trigger("startDate");
                    return;
                  }
                  field.onChange(x.startDate);
                  trigger("startDate");
                }}
                onBlur={field.onBlur}
                useSingleDate
                useMultiCalendar={false}
                useModal
                error={!!error}
                errorMessage={error?.message}
              />
            )}
          />
        </div>

        <Controller
          name="reason"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MenuDropdown
              options={reasonOptions}
              value={reasonOptions.find((r: { value: string; label: string }) => r.value === value)}
              onSelect={(option) => onChange(option.value)}
              label="Reason"
              getLabel={(option) => option.label}
              renderItem={(option) => (
                <div className="flex items-start gap-3 w-full">
                  <div className="flex-1">
                    <div className="text-body-large">{option.label}</div>
                  </div>
                </div>
              )}
            />
          )}
        />

        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={4}
              label="Note"
              value={value || ""}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
